import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import {
    setDisplayName,
    selectSettings
} from '../store/settingsSlice';
import ConfirmDialog from '../components/ConfirmDialog';
import {
    ProfileSection,
    RegionalSettingsSection,
    DataProviderSection,
    TradingProviderSection,
    NotificationsSection
} from '../components/AccountSections';

const Account = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);
    const settings = useAppSelector(selectSettings);

    const [displayNameInput, setDisplayNameInput] = useState(settings.displayName || user?.email?.split('@')[0] || '');
    const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false);

    // Simple ref for provider sections (not used for validation anymore)
    const initialStateRef = useRef({
        dataFeedProvider: '',
        tradingProvider: '',
        dataFeedConnected: false,
        connectedTradingProviders: new Set<string>()
    });

    const handleSaveSettings = () => {
        // Save display name if changed
        if (displayNameInput !== settings.displayName) {
            dispatch(setDisplayName(displayNameInput));
        }
        // Provider sections handle their own saves
    };

    const handleNavigateHome = () => {
        navigate('/home');
    };

    const handleConfirmSave = () => {
        handleSaveSettings();
        navigate('/home');
    };

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: 'var(--color-background)',
            padding: 'var(--spacing-lg)'
        }}>
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto'
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--spacing-xl)',
                    padding: 'var(--spacing-lg)',
                    backgroundColor: 'var(--color-surface)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--color-border-light)',
                    boxShadow: 'var(--shadow-sm)'
                }}>
                    <div>
                        <h1 style={{
                            fontSize: '2rem',
                            fontWeight: '700',
                            color: 'var(--color-text-primary)',
                            margin: '0 0 var(--spacing-xs) 0'
                        }}>
                            Account Settings
                        </h1>
                        <p style={{
                            fontSize: '0.875rem',
                            color: 'var(--color-text-secondary)',
                            margin: 0
                        }}>
                            Manage your profile, providers, and preferences
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                        <button
                            onClick={handleNavigateHome}
                            style={{
                                padding: 'var(--spacing-sm) var(--spacing-lg)',
                                backgroundColor: 'var(--color-surface)',
                                border: '1px solid var(--color-border-light)',
                                borderRadius: 'var(--radius-md)',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '500',
                                color: 'var(--color-text-primary)',
                                transition: 'all 200ms ease-in-out',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-xs)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-gray-50)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                            Back to Home
                        </button>
                    </div>
                </div>

                {/* Settings Sections */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 500px), 1fr))',
                    gap: 'var(--spacing-xl)'
                }}>
                    <ProfileSection
                        displayName={displayNameInput}
                        onDisplayNameChange={setDisplayNameInput}
                    />

                    <RegionalSettingsSection />

                    <DataProviderSection
                        onSaveSettings={handleSaveSettings}
                        initialStateRef={initialStateRef}
                    />

                    <TradingProviderSection
                        onSaveSettings={handleSaveSettings}
                        initialStateRef={initialStateRef}
                    />

                    <NotificationsSection />
                </div>

                {/* Footer */}
                <div style={{
                    marginTop: 'var(--spacing-xl)',
                    padding: 'var(--spacing-lg)',
                    backgroundColor: 'var(--color-primary-50)',
                    borderRadius: 'var(--radius-lg)',
                    border: '1px solid var(--color-primary-200)',
                    textAlign: 'center'
                }}>
                    <p style={{
                        fontSize: '0.875rem',
                        color: 'var(--color-primary-700)',
                        margin: 0,
                        fontWeight: '500'
                    }}>
                        💡 Changes to providers are saved automatically. Other settings are saved in the background.
                    </p>
                </div>
            </div>

            {/* Save Confirmation Dialog */}
            <ConfirmDialog
                isOpen={showSaveConfirmDialog}
                title="Save Changes?"
                message="Would you like to save your changes before returning to home?"
                confirmText="Save & Return"
                cancelText="Return Without Saving"
                onConfirm={handleConfirmSave}
                onCancel={() => {
                    setShowSaveConfirmDialog(false);
                    navigate('/home');
                }}
            />
        </div>
    );
};

export default Account;
